import React, { useState, useEffect } from "react";
import { X, Mail, Lock, User, Loader2, LogIn, ShieldCheck, ChevronLeft } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "./AuthContext";
import { Logo } from "./Logo";

const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" className="w-4 h-4" aria-hidden="true">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.26 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.3 9.14 5.38 12 5.38z" />
  </svg>
);

const GitHubIcon = () => (
  <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor" aria-hidden="true">
    <path d="M12 0C5.37 0 0 5.37 0 12c0 5.3 3.44 9.8 8.21 11.39.6.11.82-.26.82-.58 0-.29-.01-1.04-.02-2.04-3.34.72-4.04-1.61-4.04-1.61-.55-1.39-1.34-1.76-1.34-1.76-1.09-.75.08-.73.08-.73 1.21.08 1.84 1.24 1.84 1.24 1.07 1.84 2.81 1.3 3.5.99.11-.78.42-1.3.76-1.6-2.67-.3-5.47-1.33-5.47-5.93 0-1.31.47-2.38 1.24-3.22-.12-.3-.54-1.53.12-3.18 0 0 1.01-.32 3.3 1.23a11.5 11.5 0 0 1 6.01 0c2.29-1.55 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.77.84 1.24 1.91 1.24 3.22 0 4.61-2.81 5.63-5.49 5.92.43.37.81 1.1.81 2.22 0 1.6-.02 2.89-.02 3.29 0 .32.22.7.83.58C20.56 21.8 24 17.3 24 12c0-6.63-5.37-12-12-12z" />
  </svg>
);

const AppleIcon = () => (
  // The path's own coordinates run from x=-5.51 to x=20 -- wider than a
  // "0 0 24 24" viewBox, so its left ~22% was silently clipped (SVGs clip
  // to their viewBox by default). Shifting the viewBox origin left to -6
  // brings the whole glyph inside it without touching the path data.
  <svg viewBox="-6 0 26 25" className="w-4 h-4" fill="currentColor" aria-hidden="true">
    <path d="M16.365 1.43c0 1.14-.468 2.16-1.226 2.9-.83.83-2.13 1.47-3.19 1.38a3.24 3.24 0 0 1-.08-.62c0-1.09.55-2.25 1.29-3 .83-.85 2.24-1.48 3.13-1.53.04.28.07.55.07.87zm3.63 15.6c-.7 1.53-1.03 2.21-1.93 3.56-1.25 1.87-3.02 4.2-5.2 4.22-1.94.02-2.44-1.26-5.08-1.25-2.64.01-3.2 1.28-5.14 1.26-2.18-.02-3.86-2.12-5.11-3.99-3.5-5.24-3.87-11.39-1.71-14.67 1.53-2.33 3.95-3.7 6.22-3.7 2.31 0 3.76 1.27 5.67 1.27 1.85 0 2.98-1.27 5.65-1.27 2.03 0 4.18 1.1 5.71 3.01-5.02 2.75-4.2 9.9.92 11.56z" />
  </svg>
);

const LAST_AUTH_KEY = "resume_studio_last_auth_method";
const getLastAuthMethod = () => { try { return localStorage.getItem(LAST_AUTH_KEY); } catch { return null; } };
const setLastAuthMethod = (method) => { try { localStorage.setItem(LAST_AUTH_KEY, method); } catch {} };

// Lovable-inspired auth overlay (approved 2026-08-16) -- full rebuild of the
// previous tab-switcher version. title/subtitle/initialMode still work
// exactly as before for context-specific callers (the guest-generation-
// limit gate's "Save Your Resume..." copy) -- when a caller doesn't pass
// title, the new default eyebrow+dynamic-headline treatment below renders
// instead. mode "register" renamed to "signup" throughout to match this
// spec's own vocabulary. dark: classes below are written per spec but are
// inert in this app today -- theming here is prop-driven (isLight) app-
// wide, not Tailwind's class-based dark mode, and this modal has never
// taken an isLight prop (always rendered as a light card against whatever
// page is behind it, on every existing call site) -- left as literal dark:
// classes rather than inventing a second theming mechanism just for this
// one component.
export const AuthModal = ({ open, onClose, onSuccess, title, subtitle, initialMode = "login" }) => {
  const { loginWithPassword, completeMfaLogin, register, login, loginWithGithub, forgotPassword, resendVerification } = useAuth();
  const [mode, setMode] = useState(initialMode); // login | signup | forgot | mfa | verify-pending
  // Progressive email-first disclosure (approved 2026-08-16): step "email"
  // shows just the Email input + Continue; submitting a valid email
  // advances to step "password" (name too, for signup) where Continue
  // becomes the real login/register call. mfa/forgot don't use this --
  // mfa is a single code field, forgot only ever needs the email itself.
  const [step, setStep] = useState("email");

  useEffect(() => {
    if (open) { setMode(initialMode); setStep("email"); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const [resendBusy, setResendBusy] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const [googleBusy, setGoogleBusy] = useState(false);
  const [githubBusy, setGithubBusy] = useState(false);
  const [mfaPendingId, setMfaPendingId] = useState(null);
  const [mfaCode, setMfaCode] = useState("");

  useEffect(() => {
    const onOk = () => { setGoogleBusy(false); onSuccess ? onSuccess() : (onClose && onClose()); };
    const onErr = (e) => { setGoogleBusy(false); setError(e.detail || "Google sign-in failed."); };
    const onMfa = (e) => { setGoogleBusy(false); setMfaPendingId(e.detail.pendingId); setMode("mfa"); };
    window.addEventListener("auth:success", onOk);
    window.addEventListener("auth:error", onErr);
    window.addEventListener("auth:mfa-required", onMfa);
    return () => {
      window.removeEventListener("auth:success", onOk);
      window.removeEventListener("auth:error", onErr);
      window.removeEventListener("auth:mfa-required", onMfa);
    };
  }, [onSuccess, onClose]);

  if (!open) return null;

  const reset = () => { setError(""); setNotice(""); setPassword(""); };
  const close = () => { setError(""); setNotice(""); setStep("email"); onClose && onClose(); };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setNotice("");
    if (mode === "mfa") {
      setBusy(true);
      try {
        await completeMfaLogin(mfaPendingId, mfaCode.trim());
        setBusy(false);
        setLastAuthMethod("email");
        onSuccess ? onSuccess() : close();
      } catch (err) {
        setBusy(false);
        setError(err.message || "Invalid code.");
      }
      return;
    }
    if (mode === "forgot") {
      if (!email.trim() || !email.includes("@")) { setError("Enter a valid email address."); return; }
      setBusy(true);
      try {
        const res = await forgotPassword(email.trim());
        setBusy(false);
        setNotice(res.message || "If an account exists for that email, a reset link is on its way.");
      } catch (err) {
        setBusy(false);
        setError(err.message || "Something went wrong.");
      }
      return;
    }
    // Step "email": Continue just validates the address and advances to
    // the password step -- no real call yet.
    if (step === "email") {
      if (!email.trim() || !email.includes("@")) { setError("Enter a valid email address."); return; }
      setError("");
      setStep("password");
      return;
    }
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    setBusy(true);
    try {
      if (mode === "login") {
        const res = await loginWithPassword(email.trim(), password);
        if (res.mfa_required) {
          setMfaPendingId(res.pending_id);
          setMode("mfa");
          setBusy(false);
          return;
        }
      } else {
        const res = await register(email.trim(), password, name.trim());
        if (res.verification_required) {
          setBusy(false);
          setMode("verify-pending");
          return;
        }
      }
      setBusy(false);
      setLastAuthMethod("email");
      onSuccess ? onSuccess() : close();
    } catch (err) {
      setBusy(false);
      setError(err.message || "Something went wrong.");
    }
  };

  const resendFromError = async () => {
    setResendBusy(true);
    try {
      await resendVerification(email.trim());
    } finally {
      setResendBusy(false);
    }
  };

  // Apple sign-in isn't wired up on the backend yet (no loginWithApple in
  // AuthContext.jsx) -- honest "not available yet" rather than a dead or
  // fake button, same pattern AiChatPanel.jsx's own slash-command fallback
  // already uses for an unmapped command.
  const appleClick = () => toast.info("Apple sign-in isn't available yet — try Google, GitHub, or email.");

  const lastMethod = getLastAuthMethod();
  const showTabs = mode === "login" || mode === "signup";

  return (
    <div
      data-testid="auth-modal"
      className="fixed inset-0 backdrop-blur-xl bg-black/60 z-50 flex items-center justify-center p-4"
      onClick={close}
    >
      <div
        className="relative w-full max-w-md bg-white dark:bg-neutral-900 rounded-3xl p-8 shadow-2xl border border-neutral-200 dark:border-neutral-800"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          data-testid="auth-modal-close"
          onClick={close}
          className="absolute top-6 right-6 text-neutral-400 hover:text-neutral-900 dark:hover:text-white transition-colors"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        {mode === "verify-pending" ? (
          <div className="text-center py-2" data-testid="auth-verify-pending">
            <Mail className="w-10 h-10 text-[#A67C52] mx-auto mb-3" />
            <h2 className="text-2xl font-bold tracking-tight text-neutral-950 dark:text-white">Check your email</h2>
            <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-2">
              We sent a verification link to <span className="font-semibold text-neutral-700 dark:text-neutral-200">{email}</span>. Click it to finish setting up your account and sign in.
            </p>
            <button
              type="button"
              data-testid="auth-verify-pending-resend"
              disabled={resendBusy}
              onClick={resendFromError}
              className="mt-6 text-xs text-[#A67C52] hover:text-[#8A6640] font-semibold disabled:opacity-60"
            >
              {resendBusy ? "Resending…" : "Didn't get it? Resend the link"}
            </button>
            <button type="button" onClick={() => { setMode("login"); setStep("email"); reset(); }} className="block w-full text-[11px] text-neutral-500 hover:text-neutral-900 dark:hover:text-white mt-4">
              ← Back to sign in
            </button>
          </div>
        ) : (
          <>
            <Logo size="md" isLight />

            {/* Dynamic header (approved 2026-08-16) -- title/subtitle props
                (when a caller passes them) fully replace this default
                eyebrow+headline pairing, unchanged from before. */}
            <div className="mt-6">
              {title ? (
                <>
                  <h2 className="text-2xl font-bold tracking-tight text-neutral-950 dark:text-white">{title}</h2>
                  {subtitle && <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">{subtitle}</p>}
                </>
              ) : (
                <>
                  <p className="text-sm text-neutral-500 dark:text-neutral-400">Start building.</p>
                  <h2 className="text-2xl font-bold tracking-tight text-neutral-950 dark:text-white mt-1">
                    {mode === "signup" ? "Create free account" : mode === "login" ? "Log in to your account" : mode === "mfa" ? "Two-factor verification" : "Reset your password"}
                  </h2>
                </>
              )}
            </div>

            {showTabs && step === "email" && (
              <div className="mt-6 space-y-2.5">
                <div className="relative">
                  <button
                    data-testid="auth-modal-google-btn"
                    type="button"
                    disabled={googleBusy}
                    onClick={() => { setError(""); setGoogleBusy(true); setLastAuthMethod("google"); login(); }}
                    className="w-full bg-white hover:bg-neutral-50 text-neutral-800 font-semibold text-sm py-3 rounded-xl border border-neutral-200 flex items-center justify-center gap-2.5 transition-colors"
                  >
                    {googleBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <GoogleIcon />} Continue with Google
                  </button>
                  {lastMethod === "google" && (
                    <span className="absolute -top-2 right-3 bg-neutral-950 dark:bg-white text-white dark:text-neutral-950 text-[9px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full">
                      Last used
                    </span>
                  )}
                </div>
                <button
                  data-testid="auth-modal-github-btn"
                  type="button"
                  disabled={githubBusy}
                  onClick={() => { setError(""); setGithubBusy(true); setLastAuthMethod("github"); loginWithGithub(); }}
                  className="w-full bg-[#1F2328] hover:bg-[#24292f] text-white font-semibold text-sm py-3 rounded-xl border border-neutral-200 flex items-center justify-center gap-2.5 transition-colors"
                >
                  {githubBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <GitHubIcon />} Continue with GitHub
                </button>
                <button
                  data-testid="auth-modal-apple-btn"
                  type="button"
                  onClick={appleClick}
                  className="w-full bg-black hover:bg-neutral-800 text-white font-semibold text-sm py-3 rounded-xl border border-neutral-200 flex items-center justify-center gap-2.5 transition-colors"
                >
                  <AppleIcon /> Continue with Apple
                </button>

                <div className="flex items-center gap-3 py-1.5">
                  <div className="flex-1 h-px bg-neutral-200 dark:bg-neutral-800" />
                  <span className="text-[10px] uppercase tracking-widest text-neutral-400">or</span>
                  <div className="flex-1 h-px bg-neutral-200 dark:bg-neutral-800" />
                </div>
              </div>
            )}

            <form onSubmit={submit} className="mt-4 space-y-3">
              {mode === "mfa" && (
                <div className="relative">
                  <ShieldCheck className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
                  <input
                    data-testid="auth-modal-mfa-code-input"
                    type="text"
                    inputMode="numeric"
                    autoFocus
                    maxLength={6}
                    placeholder="6-digit code"
                    value={mfaCode}
                    onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ""))}
                    className="w-full bg-neutral-50 dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-xl pl-10 pr-3 py-3 text-sm text-neutral-950 dark:text-white tracking-[0.3em] placeholder:text-neutral-400 placeholder:tracking-normal focus:border-[#A67C52] focus:outline-none transition-colors"
                  />
                </div>
              )}

              {/* Step "email" (approved 2026-08-16): the one Email input the
                  spec calls for, shared across login/signup/forgot -- each
                  mode's own submit branch above decides what Continue
                  actually does with it. */}
              {mode !== "mfa" && step === "email" && (
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
                  <input
                    data-testid="auth-modal-email-input"
                    type="email"
                    required
                    autoFocus
                    placeholder="Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full bg-neutral-50 dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-xl pl-10 pr-3 py-3 text-sm text-neutral-950 dark:text-white placeholder:text-neutral-400 focus:border-[#A67C52] focus:outline-none transition-colors"
                  />
                </div>
              )}

              {/* Step "password" (approved 2026-08-16): only reached from
                  login/signup (forgot never advances past the email step
                  above) -- the locked-in email plus a "change" affordance,
                  a name field for signup, then the real password input. */}
              {(mode === "login" || mode === "signup") && step === "password" && (
                <>
                  <button
                    type="button"
                    onClick={() => { setStep("email"); setError(""); }}
                    className="flex items-center gap-1 text-xs text-neutral-500 hover:text-neutral-900 dark:hover:text-white font-medium"
                  >
                    <ChevronLeft className="w-3.5 h-3.5" /> {email}
                  </button>
                  {mode === "signup" && (
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
                      <input
                        data-testid="auth-modal-name-input"
                        type="text"
                        placeholder="Full name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="w-full bg-neutral-50 dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-xl pl-10 pr-3 py-3 text-sm text-neutral-950 dark:text-white placeholder:text-neutral-400 focus:border-[#A67C52] focus:outline-none transition-colors"
                      />
                    </div>
                  )}
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
                    <input
                      data-testid="auth-modal-password-input"
                      type="password"
                      required
                      autoFocus
                      placeholder={mode === "signup" ? "Password (min 8 characters)" : "Password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full bg-neutral-50 dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-xl pl-10 pr-3 py-3 text-sm text-neutral-950 dark:text-white placeholder:text-neutral-400 focus:border-[#A67C52] focus:outline-none transition-colors"
                    />
                  </div>
                  {mode === "login" && (
                    <button
                      type="button"
                      data-testid="auth-modal-forgot-link"
                      onClick={() => { setMode("forgot"); setStep("email"); reset(); }}
                      className="text-[11px] text-[#A67C52] hover:text-[#8A6640] font-semibold"
                    >
                      Forgot password?
                    </button>
                  )}
                </>
              )}

              {error && (
                <div className="space-y-1.5">
                  <p data-testid="auth-modal-error" className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                    {error}
                  </p>
                  {error.toLowerCase().includes("verify your email") && (
                    <button
                      type="button"
                      data-testid="auth-modal-error-resend"
                      disabled={resendBusy}
                      onClick={resendFromError}
                      className="text-[11px] text-[#A67C52] hover:text-[#8A6640] font-semibold disabled:opacity-60"
                    >
                      {resendBusy ? "Resending…" : "Resend verification link"}
                    </button>
                  )}
                </div>
              )}
              {notice && (
                <p data-testid="auth-modal-notice" className="text-xs text-emerald-600 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2">
                  {notice}
                </p>
              )}

              <button
                data-testid="auth-modal-submit"
                type="submit"
                disabled={busy}
                className="w-full bg-neutral-950 text-white dark:bg-white dark:text-neutral-950 hover:opacity-90 disabled:opacity-60 rounded-xl py-3 font-medium flex items-center justify-center gap-2 transition-opacity"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : step === "email" ? null : <LogIn className="w-4 h-4" />}
                {step === "email"
                  ? "Continue"
                  : mode === "login" ? "Log in" : mode === "signup" ? "Create account" : mode === "mfa" ? "Verify" : "Send reset link"}
              </button>

              {(mode === "forgot" || mode === "mfa") && (
                <button
                  type="button"
                  data-testid="auth-modal-back-to-login"
                  onClick={() => { setMode("login"); setStep("email"); reset(); setMfaCode(""); setMfaPendingId(null); }}
                  className="w-full text-[11px] text-neutral-500 hover:text-neutral-900 dark:hover:text-white mt-1"
                >
                  ← Back to sign in
                </button>
              )}
            </form>

            {showTabs && (
              <p className="text-center text-xs text-neutral-500 dark:text-neutral-400 mt-5">
                {mode === "login" ? "Don't have an account? " : "Already have an account? "}
                <button
                  type="button"
                  data-testid="auth-modal-mode-toggle"
                  onClick={() => { setMode(mode === "login" ? "signup" : "login"); setStep("email"); reset(); }}
                  className="text-[#A67C52] font-semibold hover:text-[#8A6640]"
                >
                  {mode === "login" ? "Sign up" : "Log in"}
                </button>
              </p>
            )}

            {/* Footer note (approved 2026-08-16) -- terms agreement + SSO
                mention, only on the entry step so it doesn't crowd the
                password/MFA/forgot steps. */}
            {showTabs && step === "email" && (
              <p className="text-center text-[10px] text-neutral-400 dark:text-neutral-500 mt-5 leading-relaxed">
                By continuing, you agree to our Terms of Service and Privacy Policy.
                <br />
                🔒 SSO available on Business and Enterprise plans
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AuthModal;
